import React, { useState } from 'react';
import classnames from 'classnames/bind';
import { Product } from '@services';
import { ProductCardBase, QuickCartControls } from '@components';
import styles from './ProductCard.module.scss';
import { ProductCardBaseSkeleton } from '../ProductCardBase/ProductCardBase.skeleton';
import { Variant } from '@services/product/interfaces';
import { LineItemDraft } from '@commercetools/platform-sdk';
import { AnalyticsListContext } from '@utils';
import { breakpoints, useMediaQuery } from 'gd-design-library';

const CN = 'product-card';
const cardClassName = `${CN}__card`;

const cx = classnames.bind(styles);

interface ProductCardProps {
  product: Product;
  isLoading?: boolean;
  search?: boolean;
  position?: number;
  handleAddToCart: (lineItemDraft: LineItemDraft, currency?: string) => Promise<void>;
  allowQuickCart?: boolean;
  displayMobileAddButton?: boolean;
  alwaysSelectMatching?: boolean;
  isExpanded?: boolean;
  className?: string;
  baseClassName?: string;
  analyticsContext: AnalyticsListContext;
}

export const ProductCard: React.FC<ProductCardProps> = ({
  product,
  isLoading,
  search=false,
  position=0,
  handleAddToCart,
  allowQuickCart=true,
  displayMobileAddButton,
  className,
  baseClassName,
  analyticsContext
}) => {
  const [ isHovered, setIsHovered ] = useState(false);
  const [ selectedVariant, setSelectedVariant ] = useState<Variant>({});
  const isMobileOrTablet = useMediaQuery(`(max-width: ${breakpoints.md})`);

  const handleMouseOver = async (): Promise<void> => {
    setIsHovered(true);
  };

  const handleMouseLeave = (): void => {
    setIsHovered(false);
  };

  return (
    !isLoading ? (
      <div
        key={ product.id }
        onMouseOver={ handleMouseOver }
        onMouseLeave={ handleMouseLeave }
        className={ cx(CN, className) }
      >
        <ProductCardBase
          isHovered={ isHovered }
          className={ cx(cardClassName, baseClassName) }
          product={ product }
          isLoading={ isLoading }
          search={ search }
          position={ position }
          images={ selectedVariant.images }
          price={ selectedVariant.price }
          analyticsContext={ analyticsContext }
          handleAddToCart={ displayMobileAddButton ? handleAddToCart : undefined }
          selectedVariant={ selectedVariant }
        />
        { allowQuickCart && !isMobileOrTablet && (
          <QuickCartControls
            product={ product }
            isOpen={ isHovered }
            selectedVariant={ selectedVariant }
            matchingVariantId={ product.matchingVariantId }
            setSelectedVariant={ setSelectedVariant }
            handleAddToCart={ handleAddToCart }
          />
        )}
      </div>
    ) : <ProductCardBaseSkeleton />
  );
};
